import { Link, useParams } from "react-router-dom";
import * as Lucide from "lucide-react";
import { Seo } from "@/components/Seo";
import { SectionHead, CtaBand, Breadcrumbs, Faq, LinkGrid } from "@/components/Shared";
import { SERVICES, PROVINCES, CITIES, BRAND } from "@/data/site";
import { url, metaServices, metaService, serviceBySlug, breadcrumbLd, faqLd, serviceLd, citiesInProv } from "@/data/content";
import NotFound from "@/pages/NotFound";

const Icon = ({ name, className }) => {
  const C = Lucide[name] || Lucide.Shield;
  return <C className={className} />;
};

export function ServicesIndex() {
  const meta = metaServices();
  return (
    <div data-testid="services-index-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-14">
      <Seo title={meta.title} description={meta.description} path="/services/" jsonLd={[breadcrumbLd([{ name: "Home", path: "/" }, { name: "Services", path: "/services/" }])]} />
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Services", path: "/services/" }]} />
      <SectionHead as="h1" eyebrow="Security Guard Services Canada" title="Every security service your operation needs — nationwide" desc="Fourteen core services delivered by licensed, insured and GPS-verified professionals in every province and territory. Click any service for national, provincial and city-level detail." />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="services-grid">
        {SERVICES.map((s) => (
          <Link key={s.slug} to={url.service(s)} data-testid={`service-card-${s.slug}`} className="group rounded-2xl border border-slate-800 bg-[#111827] p-7 transition-all hover:border-amber-500/50 hover:-translate-y-1">
            <div className="h-12 w-12 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mb-5 transition-colors group-hover:bg-amber-500">
              <Icon name={s.icon} className="h-6 w-6 text-amber-400 transition-colors group-hover:text-slate-950" />
            </div>
            <h2 className="font-display font-bold text-lg text-slate-100 mb-2 leading-snug group-hover:text-amber-400 transition-colors">{s.name}</h2>
            <p className="text-sm text-slate-400 leading-relaxed mb-4">{s.short}</p>
            <div className="flex flex-wrap gap-1.5">
              {s.kw.slice(0, 3).map((k) => (
                <span key={k} className="rounded-full bg-white/5 px-2.5 py-0.5 text-[10px] font-mono text-slate-500">{k}</span>
              ))}
            </div>
          </Link>
        ))}
      </div>
      <CtaBand heading="Not Sure Which Service You Need?" sub="Call our 24/7 dispatch desk, describe your site, and get a straight recommendation plus a written quote the same day." />
    </div>
  );
}

export function ServicePage() {
  const { serviceSlug } = useParams();
  const s = serviceBySlug(serviceSlug);
  if (!s) return <NotFound />;
  const meta = metaService(s);
  const topCities = CITIES.slice(0, 24);
  return (
    <div data-testid={`service-page-${s.slug}`}>
      <Seo title={meta.title} description={meta.description} path={url.service(s)} jsonLd={[serviceLd(s), faqLd(s.faqs.map((f) => ({ q: f.q.replaceAll("{area}", "Canada"), a: f.a.replaceAll("{area}", "Canada") }))), breadcrumbLd([{ name: "Home", path: "/" }, { name: "Services", path: "/services/" }, { name: s.name, path: url.service(s) }])]} />
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 50% 0%, rgba(245,158,11,0.12) 0%, transparent 65%)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16 relative">
          <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Services", path: "/services/" }, { name: s.name, path: url.service(s) }]} />
          <div className="flex items-center gap-4 mb-6">
            <div className="h-14 w-14 rounded-2xl bg-amber-500/10 border border-amber-500/40 flex items-center justify-center">
              <Icon name={s.icon} className="h-7 w-7 text-amber-400" />
            </div>
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-amber-500">Available Canada-Wide — 24/7</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold font-display tracking-tight text-slate-50 max-w-3xl">{s.name} in Canada</h1>
          <p className="mt-5 text-base sm:text-lg text-slate-400 leading-relaxed max-w-2xl">{s.short} {s.pitch[0]}</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href={BRAND.phoneHref} data-testid="service-call-cta" className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-6 py-3 text-sm font-bold text-slate-950 hover:bg-amber-400 transition-colors">
              <Lucide.Phone className="h-4 w-4" /> {BRAND.phone}
            </a>
            <a href={BRAND.emailHref} data-testid="service-email-cta" className="inline-flex items-center gap-2 rounded-full border border-slate-600 px-6 py-3 text-sm font-semibold text-slate-100 hover:border-amber-500/60 hover:text-amber-400 transition-colors">
              <Lucide.Mail className="h-4 w-4" /> Email for a Quote
            </a>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-14">
        <SectionHead eyebrow="What's Included" title={`${s.name}: full scope of service`} desc={s.pitch[1]} />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-14">
          {s.features.map((f) => (
            <div key={f.t} className="rounded-xl border border-slate-800 bg-[#111827] p-6">
              <Lucide.CheckCircle2 className="h-5 w-5 text-amber-400 mb-3" />
              <h3 className="font-semibold text-slate-100 mb-1.5 text-sm sm:text-base">{f.t}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>

        <LinkGrid
          testid="service-provinces"
          title={`${s.name} by Province & Territory`}
          links={PROVINCES.map((p) => ({ path: url.serviceProv(s, p), label: `${s.name} ${p.name}` }))}
        />

        <section className="my-14">
          <h2 className="text-xl sm:text-2xl font-bold font-display text-slate-50 tracking-tight mb-5">{s.name} in Major Canadian Cities</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
            {topCities.map((c) => (
              <Link key={c.slug} to={url.serviceCity(s, c)} data-testid="service-city-link" className="rounded-lg border border-slate-800 bg-[#111827] px-4 py-3 text-sm text-slate-300 transition-all hover:border-amber-500/50 hover:text-amber-400 hover:-translate-y-0.5">
                {s.name} {c.name}
              </Link>
            ))}
          </div>
          <p className="mt-4 text-sm text-slate-500">
            Plus {CITIES.length - topCities.length} more cities — browse by <Link to={url.locations()} className="text-amber-400 hover:underline">province and city</Link>.
          </p>
        </section>

        <Faq items={s.faqs.map((f) => ({ q: f.q.replaceAll("{area}", "your area"), a: f.a.replaceAll("{area}", "your area") }))} heading={`${s.name} — FAQs`} />
        <CtaBand heading={`Get ${s.name} Anywhere in Canada`} />
      </div>
    </div>
  );
}

export { citiesInProv };
